import React, { useState, useEffect, useMemo } from 'react';
import { bookingsAPI, propertiesAPI, settingsAPI } from '@/lib/api';
import { useDateFormat } from '@/context/DateFormatContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Loader2, Filter, Search, XCircle, CreditCard, DollarSign, Download, FileSpreadsheet, FileText, ChevronLeft, ChevronRight, Calendar, Star, LogOut, AlertCircle } from 'lucide-react';
import { StatusTooltip, QuickTipCard } from '@/components/QuickTip';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CHANNELS = ['direct', 'airbnb', 'booking.com', 'agoda', 'hotels.com', 'expedia', 'vrbo', 'iStayBnB'];
const STATUSES = ['provisional', 'waitlist', 'confirmed', 'checked-in', 'checked-out', 'cancelled'];
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'mpesa_manual', label: 'M-Pesa (Manual)' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const statusStyles = {
  provisional: 'bg-amber-50 text-amber-700 border-amber-200',
  waitlist: 'bg-purple-50 text-purple-700 border-purple-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'checked-in': 'bg-blue-50 text-blue-700 border-blue-200',
  'checked-out': 'bg-gray-50 text-gray-600 border-gray-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
};

const channelStyles = {
  direct: 'bg-gray-100 text-gray-700',
  airbnb: 'bg-pink-50 text-airbnb-pink',
  'booking.com': 'bg-blue-50 text-blue-700',
  agoda: 'bg-purple-50 text-purple-700',
  'hotels.com': 'bg-red-50 text-red-700',
  expedia: 'bg-yellow-50 text-yellow-800',
  vrbo: 'bg-indigo-50 text-indigo-700',
  iStayBnB: 'bg-rose-50 text-rose-700',
  marketplace: 'bg-rose-50 text-rose-700', // legacy
};

const BookingForm = ({ booking, properties, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(booking || {
    property_id: '', guest_name: '', guest_email: '', guest_phone: '',
    check_in: '', check_out: '', num_guests: 1, total_price: 0,
    channel: 'direct', channel_booking_ref: '', status: 'provisional', notes: ''
  });

  useEffect(() => {
    if (properties.length > 0 && !form.property_id) {
      setForm(prev => ({ ...prev, property_id: properties[0].property_id }));
    }
  }, [properties]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : value }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-sm font-medium">Property *</Label>
          <select name="property_id" value={form.property_id} onChange={handleChange} className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white" data-testid="booking-property-select" required>
            <option value="">Select property</option>
            {properties.map(p => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm font-medium">Guest Name *</Label>
          <Input data-testid="booking-guest-name" name="guest_name" value={form.guest_name} onChange={handleChange} required className="mt-1" />
        </div>
        <div>
          <Label className="text-sm font-medium">Guest Email</Label>
          <Input name="guest_email" value={form.guest_email} onChange={handleChange} className="mt-1" type="email" />
        </div>
        <div>
          <Label className="text-sm font-medium">Check-in *</Label>
          <Input data-testid="booking-checkin" name="check_in" type="date" value={form.check_in} onChange={handleChange} required className="mt-1" />
        </div>
        <div>
          <Label className="text-sm font-medium">Check-out *</Label>
          <Input data-testid="booking-checkout" name="check_out" type="date" value={form.check_out} onChange={handleChange} required className="mt-1" />
        </div>
        <div>
          <Label className="text-sm font-medium">Guests</Label>
          <Input name="num_guests" type="number" value={form.num_guests} onChange={handleChange} min="1" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm font-medium">Total Price</Label>
          <Input data-testid="booking-price" name="total_price" type="number" value={form.total_price} onChange={handleChange} min="0" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm font-medium">Channel</Label>
          <select name="channel" value={form.channel} onChange={handleChange} className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white">
            {CHANNELS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm font-medium">Status</Label>
          <select name="status" value={form.status} onChange={handleChange} className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white" data-testid="booking-status-select">
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm font-medium">Channel Ref</Label>
          <Input name="channel_booking_ref" value={form.channel_booking_ref} onChange={handleChange} className="mt-1" placeholder="REF-1234" />
        </div>
        <div className="col-span-2">
          <Label className="text-sm font-medium">Notes</Label>
          <Textarea name="notes" value={form.notes} onChange={handleChange} className="mt-1" rows={2} />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
        <Button data-testid="booking-save-btn" type="submit" disabled={saving} className="flex-1 bg-airbnb-pink hover:bg-airbnb-dark-pink text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {booking ? 'Update' : 'Create'} Booking
        </Button>
      </div>
    </form>
  );
};

const BookingsPage = () => {
  const { formatDate } = useDateFormat();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ property_id: '', status: '', channel: '', search: '', dateFrom: '', dateTo: '' });
  const [dateFilterType, setDateFilterType] = useState('arrival'); // 'arrival' or 'booking'
  const [showTip, setShowTip] = useState(!localStorage.getItem('bookings_tip_dismissed'));
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [baseCurrency, setBaseCurrency] = useState('USD');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  
  // Export state
  const [exporting, setExporting] = useState(false);
  
  // Manual payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ payment_method: 'cash', payment_ref: '', notes: '' });
  const [recordingPayment, setRecordingPayment] = useState(false);

  const loadData = async () => {
    try {
      const [bResp, pResp, settingsResp] = await Promise.all([
        bookingsAPI.list(), 
        propertiesAPI.list(),
        settingsAPI.get().catch(() => ({ data: {} }))
      ]);
      setBookings(bResp.data);
      setProperties(pResp.data);
      // Set currency symbol from settings
      const baseCurr = settingsResp.data?.base_currency || 'USD';
      setBaseCurrency(baseCurr);
      const symbols = { USD: '$', EUR: '€', GBP: '£', KES: 'KSh', NGN: '₦', ZAR: 'R', AED: 'د.إ', INR: '₹', JPY: '¥', AUD: 'A$', CAD: 'C$' };
      setCurrencySymbol(symbols[baseCurr] || '$');
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);
  
  const handleRecordPayment = async () => {
    if (!paymentBooking) return;
    setRecordingPayment(true);
    try {
      const token = localStorage.getItem('ihostpms_token');
      const res = await fetch(`${API_URL}/api/bookings/${paymentBooking.booking_id}/record-payment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(paymentForm)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to record payment');
      }
      const data = await res.json();
      alert(data.message);
      setPaymentDialogOpen(false);
      setPaymentBooking(null);
      setPaymentForm({ payment_method: 'cash', payment_ref: '', notes: '' });
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editingBooking) {
        await bookingsAPI.update(editingBooking.booking_id, form);
      } else {
        await bookingsAPI.create(form);
      }
      setDialogOpen(false);
      setEditingBooking(null);
      loadData();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await bookingsAPI.delete(id);
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleRequestReview = async (bookingId) => {
    try {
      const resp = await fetch(`${API_URL}/api/reviews/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ihostpms_token')}`
        },
        body: JSON.stringify({ booking_id: bookingId })
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Failed to send review request');
      }
      
      toast.success('Review request sent!', { description: 'Email sent to guest.' });
    } catch (err) {
      toast.error('Failed to send review request', { description: err.message });
    }
  };

  // Early Checkout State
  const [earlyCheckoutDialog, setEarlyCheckoutDialog] = useState(false);
  const [earlyCheckoutBooking, setEarlyCheckoutBooking] = useState(null);
  const [earlyCheckoutDate, setEarlyCheckoutDate] = useState('');
  const [earlyCheckoutPreview, setEarlyCheckoutPreview] = useState(null);
  const [earlyCheckoutLoading, setEarlyCheckoutLoading] = useState(false);
  const [earlyCheckoutReason, setEarlyCheckoutReason] = useState('');
  const [earlyCheckoutRefundMethod, setEarlyCheckoutRefundMethod] = useState('cash');
  const [earlyCheckoutNotes, setEarlyCheckoutNotes] = useState('');

  const handleEarlyCheckoutPreview = async () => {
    if (!earlyCheckoutDate || !earlyCheckoutBooking) return;
    setEarlyCheckoutLoading(true);
    try {
      const resp = await fetch(
        `${API_URL}/api/bookings/${earlyCheckoutBooking.booking_id}/early-checkout-preview?checkout_date=${earlyCheckoutDate}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('ihostpms_token')}` }}
      );
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Failed to calculate refund');
      }
      setEarlyCheckoutPreview(await resp.json());
    } catch (err) {
      toast.error('Error', { description: err.message });
      setEarlyCheckoutPreview(null);
    } finally {
      setEarlyCheckoutLoading(false);
    }
  };

  const handleEarlyCheckoutSubmit = async () => {
    if (!earlyCheckoutPreview) return;
    setEarlyCheckoutLoading(true);
    try {
      const resp = await fetch(
        `${API_URL}/api/bookings/${earlyCheckoutBooking.booking_id}/early-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('ihostpms_token')}`
          },
          body: JSON.stringify({
            actual_checkout_date: earlyCheckoutDate,
            reason: earlyCheckoutReason,
            refund_method: earlyCheckoutRefundMethod,
            notes: earlyCheckoutNotes
          })
        }
      );
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Failed to process early checkout');
      }
      const result = await resp.json();
      toast.success('Early Checkout Processed', { 
        description: `${result.nights_refunded} nights released. Refund: ${currencySymbol}${result.refund_amount.toLocaleString()}`
      });
      setEarlyCheckoutDialog(false);
      setEarlyCheckoutBooking(null);
      setEarlyCheckoutPreview(null);
      setEarlyCheckoutDate('');
      setEarlyCheckoutReason('');
      setEarlyCheckoutNotes('');
      loadData();
    } catch (err) {
      toast.error('Error', { description: err.message });
    } finally {
      setEarlyCheckoutLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return bookings.filter(b => {
      if (filters.property_id && b.property_id !== filters.property_id) return false;
      if (filters.status && b.status !== filters.status) return false;
      if (filters.channel && b.channel !== filters.channel) return false;
      if (filters.search && !b.guest_name.toLowerCase().includes(filters.search.toLowerCase())) return false;
      // Date range filter based on selected date type
      const dateField = dateFilterType === 'arrival' ? b.check_in : (b.created_at ? b.created_at.split('T')[0] : '');
      if (filters.dateFrom && dateField < filters.dateFrom) return false;
      if (filters.dateTo && dateField > filters.dateTo) return false;
      return true;
    });
  }, [bookings, filters, dateFilterType]);

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginatedBookings = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, pageSize]);

  // Export functions
  const handleExportCSV = () => {
    setExporting(true);
    try {
      const headers = ['Booking ID', 'Guest Name', 'Guest Email', 'Guest Phone', 'Property', 'Check-in', 'Check-out', 'Guests', 'Channel', 'Status', 'Amount', 'Currency', 'Created'];
      const rows = filtered.map(b => [
        b.booking_id,
        b.guest_name,
        b.guest_email || '',
        b.guest_phone || '',
        b.property_name,
        b.check_in,
        b.check_out,
        b.num_guests,
        b.channel,
        b.status,
        b.total_price,
        baseCurrency,
        b.created_at ? new Date(b.created_at).toLocaleDateString() : ''
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success(`Exported ${filtered.length} bookings to CSV`);
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = () => {
    setExporting(true);
    try {
      // Create a printable HTML content
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Bookings Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #FF385C; margin-bottom: 5px; }
            .subtitle { color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th { background: #f8f8f8; padding: 8px; text-align: left; border-bottom: 2px solid #ddd; }
            td { padding: 8px; border-bottom: 1px solid #eee; }
            .status { padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; }
            .status-confirmed { background: #d1fae5; color: #065f46; }
            .status-provisional { background: #fef3c7; color: #92400e; }
            .status-cancelled { background: #fee2e2; color: #991b1b; }
            .amount { text-align: right; font-weight: 600; }
            .footer { margin-top: 20px; text-align: center; color: #999; font-size: 10px; }
          </style>
        </head>
        <body>
          <h1>Bookings Report</h1>
          <p class="subtitle">Generated on ${new Date().toLocaleDateString()} • ${filtered.length} bookings</p>
          <table>
            <thead>
              <tr>
                <th>Guest</th>
                <th>Property</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Channel</th>
                <th>Status</th>
                <th class="amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map(b => `
                <tr>
                  <td><strong>${b.guest_name}</strong><br><span style="color:#666;font-size:10px">${b.guest_email || ''}</span></td>
                  <td>${b.property_name}</td>
                  <td>${new Date(b.check_in).toLocaleDateString()}</td>
                  <td>${new Date(b.check_out).toLocaleDateString()}</td>
                  <td>${b.channel}</td>
                  <td><span class="status status-${b.status}">${b.status}</span></td>
                  <td class="amount">${currencySymbol}${b.total_price?.toLocaleString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p class="footer">iHostPMS • Property Management System</p>
        </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
      toast.success('PDF print dialog opened');
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" /></div>;

  return (
    <div data-testid="bookings-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-airbnb-gray">Bookings</h1>
          <p className="text-airbnb-medium-gray mt-1">Manage your guest bookings</p>
        </div>
        <Button data-testid="add-booking-btn" onClick={() => { setEditingBooking(null); setDialogOpen(true); }} className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> New Booking
        </Button>
      </div>

      {/* Quick Tip */}
      {showTip && (
        <div className="mb-5">
          <QuickTipCard 
            title="Overbooking Protection" 
            tip="New bookings are created as 'Provisional' awaiting payment. If dates conflict with another provisional booking, it's added to 'Waitlist'. First guest to pay gets confirmed! Guests receive an email with a payment link."
            onDismiss={() => { setShowTip(false); localStorage.setItem('bookings_tip_dismissed', 'true'); }}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5" data-testid="booking-filters">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-airbnb-medium-gray" />
            <span className="text-sm font-semibold text-airbnb-gray">Filters</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportCSV}
              disabled={exporting || filtered.length === 0}
              className="rounded-xl h-8 text-xs"
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
              CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportPDF}
              disabled={exporting || filtered.length === 0}
              className="rounded-xl h-8 text-xs"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" />
              PDF
            </Button>
          </div>
        </div>
        
        {/* Date Filter Type Selector */}
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
          <span className="text-sm text-airbnb-medium-gray">Filter by:</span>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setDateFilterType('arrival')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                dateFilterType === 'arrival' 
                  ? 'bg-white text-airbnb-pink shadow-sm' 
                  : 'text-airbnb-medium-gray hover:text-airbnb-gray'
              }`}
              data-testid="filter-by-arrival"
            >
              Arrival Date
            </button>
            <button
              onClick={() => setDateFilterType('booking')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                dateFilterType === 'booking' 
                  ? 'bg-white text-airbnb-pink shadow-sm' 
                  : 'text-airbnb-medium-gray hover:text-airbnb-gray'
              }`}
              data-testid="filter-by-booking"
            >
              Booking Date
            </button>
          </div>
          <span className="text-xs text-airbnb-medium-gray">
            {dateFilterType === 'arrival' ? '(Check-in date)' : '(Date booking was created)'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="booking-search" placeholder="Search guest..." value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} className="pl-9 h-9 text-sm" />
          </div>
          <select value={filters.property_id} onChange={(e) => setFilters(f => ({ ...f, property_id: e.target.value }))} className="h-9 px-3 border border-gray-200 rounded-md text-sm bg-white">
            <option value="">All Properties</option>
            {properties.map(p => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))} className="h-9 px-3 border border-gray-200 rounded-md text-sm bg-white">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.channel} onChange={(e) => setFilters(f => ({ ...f, channel: e.target.value }))} className="h-9 px-3 border border-gray-200 rounded-md text-sm bg-white">
            <option value="">All Channels</option>
            {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <Input 
              type="date" 
              value={filters.dateFrom} 
              onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))} 
              className="pl-9 h-9 text-sm" 
              placeholder="From date"
              data-testid="date-from"
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <Input 
              type="date" 
              value={filters.dateTo} 
              onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))} 
              className="pl-9 h-9 text-sm" 
              placeholder="To date"
              data-testid="date-to"
            />
          </div>
        </div>
        {/* Quick date range buttons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-airbnb-medium-gray">Quick:</span>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs px-2"
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              setFilters(f => ({ ...f, dateFrom: today, dateTo: today }));
            }}
          >
            Today
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs px-2"
            onClick={() => {
              const today = new Date();
              const weekAgo = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
              setFilters(f => ({ ...f, dateFrom: weekAgo, dateTo: new Date().toISOString().split('T')[0] }));
            }}
          >
            Last 7 days
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs px-2"
            onClick={() => {
              const today = new Date();
              const monthAgo = new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
              setFilters(f => ({ ...f, dateFrom: monthAgo, dateTo: new Date().toISOString().split('T')[0] }));
            }}
          >
            Last 30 days
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs px-2"
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
              setFilters(f => ({ ...f, dateFrom: firstDay, dateTo: new Date().toISOString().split('T')[0] }));
            }}
          >
            This month
          </Button>
          {(filters.dateFrom || filters.dateTo) && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs px-2 text-red-500 hover:text-red-600"
              onClick={() => setFilters(f => ({ ...f, dateFrom: '', dateTo: '' }))}
            >
              <XCircle className="w-3 h-3 mr-1" /> Clear dates
            </Button>
          )}
        </div>
      </div>

      {/* Bookings List - Cards on Mobile, Table on Desktop */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-airbnb-medium-gray">No bookings found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100" data-testid="bookings-cards-mobile">
              {paginatedBookings.map((b) => {
                const nights = Math.ceil((new Date(b.check_out) - new Date(b.check_in)) / (1000 * 60 * 60 * 24));
                return (
                  <div 
                    key={b.booking_id} 
                    className="p-4 hover:bg-gray-50/50 transition-colors"
                    data-testid={`booking-card-${b.booking_id}`}
                  >
                    {/* Card Header: Guest + Status */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-airbnb-gray truncate">{b.guest_name}</h3>
                        <p className="text-xs text-airbnb-medium-gray truncate">{b.property_name}</p>
                      </div>
                      <span className={`ml-2 px-2.5 py-1 rounded-full text-xs font-semibold capitalize border flex-shrink-0 ${statusStyles[b.status] || 'bg-gray-100'}`}>
                        {b.status}
                      </span>
                    </div>
                    
                    {/* Dates Row */}
                    <div className="flex items-center gap-2 text-sm text-airbnb-medium-gray mb-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{formatDate(b.check_in)}</span>
                      <span className="text-gray-300">→</span>
                      <span>{formatDate(b.check_out)}</span>
                      <span className="text-xs text-gray-400">({nights}N)</span>
                    </div>
                    
                    {/* Footer: Channel, Price, Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${channelStyles[b.channel] || 'bg-gray-100'}`}>
                          {b.channel}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-airbnb-gray">{currencySymbol}{b.total_price?.toLocaleString()}</span>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-airbnb-medium-gray hover:text-blue-600" 
                            onClick={() => { setEditingBooking(b); setDialogOpen(true); }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-airbnb-medium-gray hover:text-red-600" 
                            onClick={() => handleDelete(b.booking_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons for special statuses */}
                    {(['provisional', 'waitlist'].includes(b.status) || b.status === 'checked-in' || (b.status === 'checked-out' && b.guest_email)) && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                        {['provisional', 'waitlist'].includes(b.status) && (
                          <Button 
                            variant="outline"
                            size="sm" 
                            className="h-8 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50" 
                            onClick={() => { setPaymentBooking(b); setPaymentDialogOpen(true); }}
                          >
                            <DollarSign className="w-3.5 h-3.5 mr-1" />
                            Record Payment
                          </Button>
                        )}
                        {b.status === 'checked-in' && (
                          <Button 
                            variant="outline"
                            size="sm" 
                            className="h-8 text-xs text-orange-600 border-orange-200 hover:bg-orange-50" 
                            onClick={() => {
                              setEarlyCheckoutBooking(b);
                              setEarlyCheckoutDate('');
                              setEarlyCheckoutPreview(null);
                              setEarlyCheckoutDialog(true);
                            }}
                          >
                            <LogOut className="w-3.5 h-3.5 mr-1" />
                            Early Checkout
                          </Button>
                        )}
                        {b.status === 'checked-out' && b.guest_email && (
                          <Button 
                            variant="outline"
                            size="sm" 
                            className="h-8 text-xs text-yellow-600 border-yellow-200 hover:bg-yellow-50" 
                            onClick={() => handleRequestReview(b.booking_id)}
                          >
                            <Star className="w-3.5 h-3.5 mr-1" />
                            Request Review
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" data-testid="bookings-table">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase">Channel</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase">Guest</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase">Property</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase">Dates</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase">Status</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-airbnb-medium-gray uppercase">Amount</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-airbnb-medium-gray uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {paginatedBookings.map((b) => (
                    <tr key={b.booking_id} className="hover:bg-gray-50/50 transition-colors" data-testid={`booking-row-${b.booking_id}`}>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${channelStyles[b.channel] || 'bg-gray-100'}`}>
                          {b.channel}
                        </span>
                        {b.channel_booking_ref && <div className="text-[10px] text-gray-400 mt-0.5">#{b.channel_booking_ref}</div>}
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-sm font-medium text-airbnb-gray">{b.guest_name}</div>
                        {b.guest_email && <div className="text-xs text-airbnb-medium-gray">{b.guest_email}</div>}
                      </td>
                      <td className="px-5 py-3 text-sm text-airbnb-medium-gray">{b.property_name}</td>
                      <td className="px-5 py-3 text-sm text-airbnb-medium-gray">
                        <div>{formatDate(b.check_in)}</div>
                        <div className="text-[10px] text-gray-400">to {formatDate(b.check_out)}</div>
                      </td>
                      <td className="px-5 py-3">
                        <StatusTooltip status={b.status}>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize border cursor-help ${statusStyles[b.status] || 'bg-gray-100'}`}>
                            {b.status}
                          </span>
                        </StatusTooltip>
                      </td>
                      <td className="px-5 py-3 text-right text-sm font-semibold text-airbnb-gray">{currencySymbol}{b.total_price?.toLocaleString()}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {/* Record Payment button for provisional/waitlist bookings */}
                          {['provisional', 'waitlist'].includes(b.status) && (
                            <Button 
                              data-testid={`record-payment-${b.booking_id}`} 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" 
                              onClick={() => { setPaymentBooking(b); setPaymentDialogOpen(true); }}
                              title="Record manual payment"
                            >
                              <DollarSign className="w-3.5 h-3.5 mr-1" />
                              <span className="text-xs">Record Payment</span>
                            </Button>
                          )}
                          {/* Request Review button for checked-out bookings with email */}
                          {b.status === 'checked-out' && b.guest_email && (
                            <Button 
                              data-testid={`request-review-${b.booking_id}`} 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 px-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50" 
                              onClick={() => handleRequestReview(b.booking_id)}
                              title="Request guest review"
                            >
                              <Star className="w-3.5 h-3.5 mr-1" />
                              <span className="text-xs">Request Review</span>
                            </Button>
                          )}
                          {/* Early Checkout button for checked-in bookings */}
                          {b.status === 'checked-in' && (
                            <Button 
                              data-testid={`early-checkout-${b.booking_id}`} 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50" 
                              onClick={() => {
                                setEarlyCheckoutBooking(b);
                                setEarlyCheckoutDate('');
                                setEarlyCheckoutPreview(null);
                                setEarlyCheckoutReason('');
                                setEarlyCheckoutNotes('');
                                setEarlyCheckoutDialog(true);
                              }}
                              title="Process early checkout"
                            >
                              <LogOut className="w-3.5 h-3.5 mr-1" />
                              <span className="text-xs">Early Checkout</span>
                            </Button>
                          )}
                          <Button data-testid={`edit-booking-${b.booking_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-airbnb-medium-gray hover:text-blue-600" onClick={() => { setEditingBooking(b); setDialogOpen(true); }}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button data-testid={`delete-booking-${b.booking_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-airbnb-medium-gray hover:text-red-600" onClick={() => handleDelete(b.booking_id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-airbnb-medium-gray">Show</span>
                  <select 
                    value={pageSize} 
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="h-8 px-2 border border-gray-200 rounded-md text-sm bg-white"
                    data-testid="page-size-select"
                  >
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <span className="text-sm text-airbnb-medium-gray">per page</span>
                </div>
                <span className="text-sm text-airbnb-medium-gray">
                  Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filtered.length)} of {filtered.length} bookings
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="h-8 px-2 text-xs"
                >
                  First
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium text-airbnb-gray px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                  data-testid="next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="h-8 px-2 text-xs"
                >
                  Last
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Booking Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBooking ? 'Edit Booking' : 'New Booking'}</DialogTitle>
            <DialogDescription>
              {editingBooking ? 'Update booking details' : 'Create a new booking. Guest will receive payment link via email.'}
            </DialogDescription>
          </DialogHeader>
          <BookingForm booking={editingBooking} properties={properties} onSave={handleSave} onCancel={() => setDialogOpen(false)} saving={saving} />
        </DialogContent>
      </Dialog>

      {/* Record Manual Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              Record Manual Payment
            </DialogTitle>
            <DialogDescription>
              Record a payment made outside the system (cash, bank transfer, etc.)
            </DialogDescription>
          </DialogHeader>
          
          {paymentBooking && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-800">{paymentBooking.guest_name}</p>
                <p className="text-slate-500">{paymentBooking.property_name}</p>
                <p className="text-slate-500">{paymentBooking.check_in} to {paymentBooking.check_out}</p>
                <p className="font-bold text-emerald-600 mt-2">{currencySymbol}{paymentBooking.total_price?.toLocaleString()}</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Payment Method *</Label>
                <select
                  value={paymentForm.payment_method}
                  onChange={e => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))}
                  className="w-full mt-1 h-10 px-3 border border-slate-200 rounded-lg text-sm"
                  data-testid="payment-method-select"
                >
                  {PAYMENT_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Payment Reference / Receipt #</Label>
                <Input
                  value={paymentForm.payment_ref}
                  onChange={e => setPaymentForm(f => ({ ...f, payment_ref: e.target.value }))}
                  placeholder="e.g., TRX123456, Receipt #789"
                  className="mt-1"
                  data-testid="payment-ref-input"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Notes (optional)</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Any additional notes about this payment"
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleRecordPayment} 
                  disabled={recordingPayment}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  data-testid="confirm-record-payment"
                >
                  {recordingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  Confirm Payment
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Guest will receive confirmation email with property details and guest portal link.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Early Checkout Dialog */}
      <Dialog open={earlyCheckoutDialog} onOpenChange={setEarlyCheckoutDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5 text-orange-500" />
              Early Checkout
            </DialogTitle>
            <DialogDescription>
              Process early checkout and calculate refund for unused nights
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Booking Info */}
            {earlyCheckoutBooking && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-800">{earlyCheckoutBooking.guest_name}</p>
                <p className="text-sm text-slate-600">
                  Original: {earlyCheckoutBooking.check_in?.slice(0, 10)} → {earlyCheckoutBooking.check_out?.slice(0, 10)}
                </p>
                <p className="text-sm text-slate-600">Total: {currencySymbol}{earlyCheckoutBooking.total_price?.toLocaleString()}</p>
              </div>
            )}

            {/* Early Checkout Date */}
            <div>
              <Label className="text-sm font-medium">New Checkout Date *</Label>
              <Input
                type="date"
                value={earlyCheckoutDate}
                onChange={(e) => {
                  setEarlyCheckoutDate(e.target.value);
                  setEarlyCheckoutPreview(null);
                }}
                min={earlyCheckoutBooking?.check_in?.slice(0, 10)}
                max={earlyCheckoutBooking?.check_out?.slice(0, 10)}
                className="mt-1"
                data-testid="early-checkout-date"
              />
            </div>

            {/* Calculate Refund Button */}
            <Button
              onClick={handleEarlyCheckoutPreview}
              disabled={!earlyCheckoutDate || earlyCheckoutLoading}
              variant="outline"
              className="w-full"
            >
              {earlyCheckoutLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Calculate Refund
            </Button>

            {/* Refund Preview */}
            {earlyCheckoutPreview && (
              <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-500" />
                  Refund Calculation
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-slate-600">Nights stayed:</p>
                  <p className="font-medium text-slate-800">{earlyCheckoutPreview.nights_stayed}</p>
                  
                  <p className="text-slate-600">Nights refunded:</p>
                  <p className="font-medium text-slate-800">{earlyCheckoutPreview.nights_remaining}</p>
                  
                  <p className="text-slate-600">Per night rate:</p>
                  <p className="font-medium text-slate-800">{currencySymbol}{earlyCheckoutPreview.price_per_night?.toLocaleString()}</p>
                  
                  <p className="text-slate-600">Policy ({earlyCheckoutPreview.cancellation_policy}):</p>
                  <p className="font-medium text-slate-800">{earlyCheckoutPreview.refund_percentage}% refund</p>
                </div>
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-slate-800">Calculated Refund:</p>
                    <p className="text-xl font-bold text-emerald-600">
                      {currencySymbol}{earlyCheckoutPreview.calculated_refund?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Details (only show after preview) */}
            {earlyCheckoutPreview && (
              <>
                <div>
                  <Label className="text-sm font-medium">Reason for Early Checkout</Label>
                  <Input
                    value={earlyCheckoutReason}
                    onChange={(e) => setEarlyCheckoutReason(e.target.value)}
                    placeholder="e.g., Emergency, Changed plans"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">Refund Method</Label>
                  <select
                    value={earlyCheckoutRefundMethod}
                    onChange={(e) => setEarlyCheckoutRefundMethod(e.target.value)}
                    className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="original_payment">Reverse to Original Payment</option>
                    <option value="credit">Account Credit</option>
                  </select>
                </div>

                <div>
                  <Label className="text-sm font-medium">Notes</Label>
                  <Textarea
                    value={earlyCheckoutNotes}
                    onChange={(e) => setEarlyCheckoutNotes(e.target.value)}
                    placeholder="Any additional notes..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setEarlyCheckoutDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleEarlyCheckoutSubmit}
                disabled={!earlyCheckoutPreview || earlyCheckoutLoading}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                data-testid="confirm-early-checkout"
              >
                {earlyCheckoutLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
                Process Checkout
              </Button>
            </div>

            {earlyCheckoutPreview && (
              <p className="text-xs text-slate-500 text-center">
                This will release {earlyCheckoutPreview.nights_remaining} night(s) for new bookings.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsPage;
